import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import CustomTable from "../Reuseables/Tables/CustomTable";
import {
  createRecommendation,
  editMedia,
  getMediaUrl,
  getRecommendations,
} from "../../api/training";
import { useTranslation } from "react-i18next";
import InputField from "../Reuseables/Fields/InputField";
import { Form, Formik, useFormik, useFormikContext } from "formik";
import ReactMde from "react-mde";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import "./react-mde-all.css";
import ReactMarkdown from "react-markdown";
import { styled } from "@mui/styles";
import MultiSelectTagField from "../Reuseables/Fields/MultiSelectTagField";
import { recommendationValidation } from "../../helpers/validationSchemes";
import SelectField from "../Reuseables/Fields/SelectField";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useApplicationContext } from "../ContextProviders/ApplicationContext";
import { TableColumn } from "../../types/tableColumn";
import { Recommendation } from "../../types/recommendation";
import { groupedActivityTags } from "../../helpers/selectOptions";
import { useOrganizationContext } from "../ContextProviders/OrganizationContext";
import { DeviceFrameset } from "react-device-frameset";
import "react-device-frameset/styles/marvel-devices.min.css";
import { use } from "i18next";
export default function TrainingPage() {
  const [pageSize, _] = useState(1000); //TODO for now we just fetch all
  const [continuationToken, setContinuationToken] = useState<string>("");
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { activeWorkspace } = useOrganizationContext();
  const { i18n, t } = useTranslation();

  const emptyTranslationTemplate = {
    en: { title: "", body: "" },
    da: { title: "", body: "" },
    de: { title: "", body: "" },
    es: { title: "", body: "" },
    fr: { title: "", body: "" },
    nb: { title: "", body: "" },
  };
  const emptyRecommendation: Recommendation = {
    selectedLanguage: "en",
    tenantId: activeWorkspace.workspaceId,
    recommendationId: "",
    tags: [],
    thumbnail: null,
    thumbnailName: "",
    videoName: "",
    video: null,
    edited: null,
    translations: emptyTranslationTemplate,
  };

  const [initValues, setInitValues] =
    useState<Recommendation>(emptyRecommendation);
  const fetchRecommendations = async (controller: AbortController) => {
    try {
      const result = await getRecommendations(
        pageSize,
        continuationToken,
        controller
      );
      setContinuationToken(result.continuationToken);
      setRecommendations([...result.items]);
      setIsLoading(false);
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === "AbortError") console.log("Request aborted!");
      } else console.error(e);
    }
  };
  const handleClose = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    setIsLoading(true);
    let controller = new AbortController();
    fetchRecommendations(controller);
    return () => {
      setIsLoading(false);
      controller.abort();
    };
  }, []);

  const columns: TableColumn[] = [
    {
      label: t("labels.title"),
      key: `translations.${i18n.language}.title`,
      sort: true,
    },
    {
      label: "Tags",
      key: "tags",
      Cell: ({ value }) => {
        let values: string[] = [];
        value.forEach((element: string) => {
          values.push(t(element));
        });
        return values.join(", ");
      },
      sort: true,
    },
    {
      label: t("edited"),
      key: "edited",
      sort: true,
      Cell: ({ value }) => {
        return new Date(value).toLocaleString();
      },
    },
    {
      key: "recommendationId",
      sort: false,
      label: t("labels.edit"),
      Cell: ({ value }) => {
        return (
          <IconButton
            onClick={() => {
              var rec = recommendations.find(
                (r) => r.recommendationId === value
              );
              if (!rec) return;
              rec.translations = {
                ...emptyTranslationTemplate,
                ...rec.translations,
              };
              setInitValues({ ...rec, selectedLanguage: "en" });
              setOpenDialog(true);
            }}
          >
            <ModeEditIcon />
          </IconButton>
        );
      },
    },
  ];

  return (
    <>
      {isLoading ? (
        <Skeleton sx={{ mt: 2, height: "95vh" }} />
      ) : (
        <Grid container mt={2}>
          <Dialog
            onClose={handleClose}
            open={openDialog}
            maxWidth="md"
            fullWidth
          >
            <RecommendationDialog
              initValues={initValues}
              handleClose={handleClose}
              recommendations={recommendations}
              setRecommendations={setRecommendations}
            />
          </Dialog>
          <Grid item flexGrow={1} />
          <Grid item xs={12}>
            <Button
              onClick={() => {
                setInitValues(emptyRecommendation);
                setOpenDialog(true);
              }}
              sx={{ marginBottom: 2 }}
              variant="contained"
            >
              {t("create-new")}
            </Button>
            <CustomTable
              title={t("recommendations")}
              defaultRowsPrPage={10}
              columns={columns}
              data={recommendations}
            />
          </Grid>
          <Grid item flexGrow={1} />
        </Grid>
      )}
    </>
  );
}

type RecommendationDialogProps = {
  initValues: Recommendation;
  setRecommendations: (recommendations: Recommendation[]) => void;
  recommendations: Recommendation[];
  handleClose: () => void;
};

const RecommendationDialog: FC<RecommendationDialogProps> = ({
  initValues,
  setRecommendations,
  recommendations,
  handleClose,
}) => {
  const [selectedTab, setSelectedTab] = useState<"write" | "preview">("write");
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();
  var isEdit = initValues.recommendationId !== "";

  const languages = [
    { value: "en", label: "English" },
    { value: "da", label: "Dansk" },
    { value: "de", label: "Deutsch" },
    { value: "es", label: "Español" },
    { value: "fr", label: "Français" },
    { value: "nb", label: "Norsk" },
  ];

  const handleMediaUpdate = async (
    recommendationId: string,
    media: File | null,
    setFieldValue: (fieldName: string, fieldValue: any) => void,
    tenantId?: string
  ) => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      if (!media) {
        console.error("media is null");
        return;
      }
      const fileType = media?.type;

      if (fileType?.startsWith("video/")) {
        formData.append("video", media);
      } else {
        formData.append("thumbnail", media);
      }

      var res = await editMedia(recommendationId, formData, tenantId);

      let index = recommendations.findIndex(
        (r) => r.recommendationId === res.recommendationId
      );
      let newArray = [...recommendations];
      if (index === -1) {
        newArray.push(res);
      } else {
        newArray[index] = res;
      }
      setRecommendations(newArray);
      if (fileType.startsWith("video/")) {
        setFieldValue("videoName", media.name);
      } else {
        setFieldValue("thumbnailName", media.name);
      }

      showSnackbar(t("media-updated"), "success");

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };
  const handleSubmit = async (values: Recommendation) => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      formData.append("json", JSON.stringify(values));
      if (values.thumbnail) formData.append("thumbnail", values.thumbnail);

      formData.append("language", values.selectedLanguage);

      if (!values.recommendationId) {
        if (values.video) formData.append("video", values.video);
      }

      var res = await createRecommendation(formData);
      setIsLoading(false);
      handleClose();
      if (values.recommendationId) {
        let index = recommendations.findIndex(
          (r) => r.recommendationId === res.recommendationId
        );
        let newArray = [...recommendations];
        if (index === -1) {
          newArray.push(res);
        } else {
          newArray[index] = res;
        }

        setRecommendations(newArray);
        showSnackbar(t("recommendation-edited"), "success");
      } else {
        setRecommendations([...recommendations, res]);
        showSnackbar(t("recommendation-created"), "success");
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        showSnackbar(error.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Formik
      initialValues={initValues}
      onSubmit={handleSubmit}
      validateOnMount
      validationSchema={recommendationValidation}
    >
      {({
        values,
        setFieldValue,
        getFieldProps,
        resetForm,
        isValid,
        dirty,
      }) => (
        <Form id="recId">
          <DialogTitle>{t("create-new-recommendation")}</DialogTitle>
          <DialogContent>
            <Grid container sx={{ flexDirection: "row" }} spacing={1}>
              <Grid item xs>
                <SelectField
                  fullWidth
                  disabled={isLoading}
                  name="selectedLanguage"
                  sx={{ marginTop: 1 }}
                  label={"language"}
                  data={languages}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    resetForm();
                    setFieldValue("selectedLanguage", e.target.value);
                  }}
                />

                <InputField
                  sx={{ marginTop: 2, marginBottom: 1 }}
                  name={`translations.${values.selectedLanguage}.title`}
                  label={"labels.title"}
                  disabled={isLoading}
                />
                <Typography marginTop={1}>
                  {t("labels.description")}:
                </Typography>
                <ReactMde
                  value={
                    getFieldProps(
                      `translations.${values.selectedLanguage}.body`
                    ).value
                  }
                  onChange={(content) => {
                    setFieldValue(
                      `translations.${values.selectedLanguage}.body`,
                      content
                    );
                  }}
                  selectedTab={selectedTab}
                  onTabChange={setSelectedTab}
                  generateMarkdownPreview={(markdown) =>
                    Promise.resolve(<ReactMarkdown>{markdown}</ReactMarkdown>)
                  }
                />
                <InputField
                  disabled
                  name={`thumbnail`}
                  sx={{ marginTop: 2 }}
                  label="Thumbnail"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    value: isEdit
                      ? values.thumbnailName
                      : values.thumbnail?.name,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled={isLoading} component="label">
                          <FileUploadIcon />
                          <VisuallyHiddenInput
                            type="file"
                            accept=".jpeg, .jpg, .png"
                            onChange={(e) => {
                              if (e.target.files) {
                                setFieldValue(`thumbnail`, e.target.files[0]);
                              }
                            }}
                          />
                        </IconButton>
                        {isEdit && (
                          <Button
                            variant="contained"
                            disabled={!values.thumbnail}
                            onClick={async () => {
                              await handleMediaUpdate(
                                values.recommendationId,
                                values.thumbnail,
                                setFieldValue,
                                values.tenantId
                              );
                            }}
                          >
                            {t("upload")}
                          </Button>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                <InputField
                  disabled
                  InputLabelProps={{ shrink: true }}
                  name={`video`}
                  sx={{ marginTop: 2 }}
                  label="upload"
                  InputProps={{
                    value: isEdit ? values.videoName : values.video?.name,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled={isLoading} component="label">
                          <FileUploadIcon />
                          <VisuallyHiddenInput
                            type="file"
                            accept=".mp4, .avi, .mov"
                            onChange={(e) => {
                              if (e.target.files) {
                                setFieldValue(`video`, e.target.files[0]);
                                e.target.focus();
                              }
                            }}
                          />
                        </IconButton>
                        {isEdit && (
                          <Button
                            variant="contained"
                            disabled={!values.video}
                            onClick={async () => {
                              await handleMediaUpdate(
                                values.recommendationId,
                                values.video,
                                setFieldValue,
                                values.tenantId
                              );
                            }}
                          >
                            {t("upload")}
                          </Button>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                <MultiSelectTagField
                  isDisabled={isLoading}
                  sx={{ marginTop: 2 }}
                  isFullWidth={true}
                  label={"labels.tags"}
                  name="tags"
                  data={groupedActivityTags}
                />
                {isLoading ? (
                  <CircularProgress sx={{ marginTop: 2 }} />
                ) : (
                  <Button
                    sx={{ marginTop: 2 }}
                    type="submit"
                    variant="contained"
                    disabled={isEdit ? !dirty : !isValid}
                  >
                    {t("buttons.save")}
                  </Button>
                )}
              </Grid>
              <Grid item xs>
                <IphonePreview isEdit={isEdit} />
              </Grid>
            </Grid>
          </DialogContent>
        </Form>
      )}
    </Formik>
  );
};
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});


type IphonePreviewProps = {
  isEdit: boolean
}

const IphonePreview : React.FC<IphonePreviewProps> = ({isEdit}) => {
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const { values } = useFormikContext<any>();
  const { activeWorkspace} = useOrganizationContext();
  useEffect(() => {
    if (values.video instanceof File) {
      const url = URL.createObjectURL(values.video);
      setVideoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setVideoPreviewUrl(null);
    }
  }, [values.video]);
  

  useEffect(() => {

    const fetchMediaUrl = async() => {

     let result = await getMediaUrl(values.recommendationId, "video", activeWorkspace.workspaceId)
     setVideoPreviewUrl(result.url);
    }
    if(isEdit){
      
      fetchMediaUrl();
    }
  },[])

  return (
    <Box display={"flex"} justifyContent={"center"}>
      <DeviceFrameset device="iPhone 8 Plus" color="black" height={500} width={281}>
        <video  controls autoPlay={isEdit ? false : true} src={videoPreviewUrl ?? ""} style={{height: "50%", width: 275, padding: 1}}></video>
        <Box padding={1} sx={{width: 275, maxHeight:"50%"}} overflow={"auto"}>
          {/* {JSON.stringify(values.videoLink)} */}
        <ReactMarkdown>
          {values.translations?.[values.selectedLanguage]?.body || ""}
        </ReactMarkdown>
        </Box>
      </DeviceFrameset>
    </Box>
  );
};
